import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { TextItem } from 'pdfjs-dist/types/src/display/api';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

interface PDFViewerProps {
    pdfBlob: Blob;
    searchEnabled?: boolean;
    highlightColor?: string;
    searchPlaceholder?: string;
    showSearchSummary?: boolean;
    defaultZoom?: number;
}

const StyledButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            padding: '5px 10px',
            backgroundColor: '#ffa500',
            border: 'none',
            borderRadius: '4px',
            color: '#fff',
            cursor: disabled ? 'not-allowed' : 'pointer',
            marginRight: '10px',
            transition: 'background-color 0.3s ease',
        }}
    >
        {children}
    </button>
);

export const PDFViewer: React.FC<PDFViewerProps> = ({
    pdfBlob,
    searchEnabled = true,
    highlightColor = '#ffa500',
    searchPlaceholder = 'Search...',
    showSearchSummary = true,
    defaultZoom = 1.0,
}) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchResults, setSearchResults] = useState<number[]>([]);
    const [highlightedPage, setHighlightedPage] = useState<number | null>(null);
    const textContentRef = useRef<string[]>([]);

    useEffect(() => {
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [pdfBlob]);

const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages);

    if (searchEnabled) {
        // Asenkron işlemi ayrı bir fonksiyona taşı
        const loadTextContent = async () => {
            const allText = await Promise.all(
                Array.from({ length: pdf.numPages }, async (_, i) => {
                    const page = await pdf.getPage(i + 1);
                    const textContent = await page.getTextContent();
                    return textContent.items
                        .filter((item): item is TextItem => 'str' in item)
                        .map((item) => item.str)
                        .join(' ');
                })
            );
            textContentRef.current = allText;
        };

        // Asenkron işlemi başlat, ancak sonuç bekleme
        void loadTextContent();
    }
}, [searchEnabled]);

    const handleSearch = useCallback(() => {
        if (!searchEnabled) return;

        const results: number[] = [];
        textContentRef.current.forEach((text, index) => {
            if (text.toLowerCase().includes(searchTerm.toLowerCase())) {
                results.push(index + 1);
            }
        });

        setSearchResults(results);
        setHighlightedPage(results.length > 0 ? results[0] : null);
    }, [searchEnabled, searchTerm]);

    // Debounce Effect for Search
    useEffect(() => {
        if (!searchEnabled) return;
        if (searchTerm.trim().length === 0) {
            setSearchResults([]);
            setHighlightedPage(null);
            return;
        }

        const timeout = setTimeout(handleSearch, 300);
        return () => { clearTimeout(timeout); };
    }, [searchTerm, handleSearch, searchEnabled]);

    const goToNextMatch = useCallback(() => {
        if (searchResults.length > 0 && highlightedPage !== null) {
            const currentIndex = searchResults.indexOf(highlightedPage);
            const nextIndex = (currentIndex + 1) % searchResults.length;
            setHighlightedPage(searchResults[nextIndex]);
        }
    }, [searchResults, highlightedPage]);

    if (!pdfUrl) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>📄 Loading PDF...</div>;
    }

    return (
        <div style={{ height: '100vh', width: '100%', overflow: 'auto' }}>
            {searchEnabled && (
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', marginBottom: '10px' }}>
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); }}
                        style={{
                            marginRight: '10px',
                            padding: '5px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            width: '250px',
                        }}
                    />
                    <StyledButton onClick={handleSearch}>Search</StyledButton>
                    <StyledButton onClick={goToNextMatch} disabled={searchResults.length === 0}>
                        Next
                    </StyledButton>
                    {showSearchSummary && searchResults.length > 0 && (
                        <span style={{ marginLeft: '20px' }}>
                            {`Found ${searchResults.length} match${searchResults.length > 1 ? 'es' : ''}`}
                        </span>
                    )}
                </div>
            )}

            <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from({ length: numPages ?? 0 }).map((_, index) => (
                    <div
                        key={`page_${index + 1}`}
                        style={{
                            marginBottom: '15px',
                            borderRadius: '10px',
                            boxShadow:
                                highlightedPage === index + 1
                                    ? `0 0 15px 6px ${highlightColor}`
                                    : '0 0 5px rgba(0, 0, 0, 0.1)',
                            scrollMargin: '50px',
                            transition: 'box-shadow 0.4s ease, transform 0.2s ease',
                            transform: highlightedPage === index + 1 ? 'scale(1.02)' : 'scale(1)',
                        }}
                    >
                        <Page
                            pageNumber={index + 1}
                            renderAnnotationLayer
                            renderTextLayer
                            className={highlightedPage === index + 1 ? 'highlighted-page' : ''}
                            scale={defaultZoom}
                        />
                    </div>
                ))}
            </Document>
        </div>
    );
};
